import {APIClient} from '../utils/APIClient';
import {isOk} from '../utils/apiResponse';
import {API_URL} from '../settings/local_settings';

// Shopify por link de instalación (tanda 23/10/2026, `contrato.md` §3). Mientras Shopify no verifica la app
// pública, cada tienda se conecta con una app personalizada que fede crea para ella: la agencia PIDE el link,
// fede lo prepara desde el CC y la agencia lo ve en Conexiones (y le llega por mail).
//
// `GET shopify/custom-app/requests/` → `{status: 'OK', data: {requests: [...]}}`: los pedidos abiertos de la
//   agencia, el más nuevo primero. Con el flag apagado, lista vacía.
// `POST shopify/custom-app/requests/` con `{shop, agency_client_id}` → `{status: 'OK', data: {request,
//   outcome, notify_email}}`. `outcome`: `created` | `existing` | `renewed`.
//
// El texto de un error sale del `error_code`, NUNCA del `error_message`: puede ser el `detail` en inglés de
// DRF o un relleno del propio `APIClient` (gotchas del CLAUDE.md del repo).

const REQUESTS_URL = `${API_URL}shopify/custom-app/requests/`;

export const OUTCOME_CREATED = 'created';
export const OUTCOME_EXISTING = 'existing';
export const OUTCOME_RENEWED = 'renewed';

const ERROR_KEY_BY_CODE = {
    INVALID_SHOP: 'shopify_link.error_invalid_shop',
};
const ERROR_GENERIC_KEY = 'shopify_link.error_generic';

/**
 * La clave i18n del error de un pedido. Sólo `INVALID_SHOP` tiene texto propio: el 404 (flag apagado, plan
 * sin Shopify, cliente ajeno), el 422 del portal y la red caen al genérico. Ninguno dice nada de otra agencia.
 */
export function errorKeyOf(response) {
    const code = response && response.error_code;
    return ERROR_KEY_BY_CODE[code] || ERROR_GENERIC_KEY;
}

/**
 * El id del cliente como número, o `null`. El selector de clientes de la ficha lo entrega como el `value` de
 * un `<option>` (un string, `''` sin elegir).
 */
export function clientIdOf(value) {
    const id = Number(value);
    return value !== null && value !== '' && Number.isInteger(id) && id > 0 ? id : null;
}

/** Los pedidos abiertos de la agencia: `{ok: true, requests}` o `{ok: false}`. */
export function listShopifyLinkRequests() {
    return new APIClient(REQUESTS_URL).call('GET').then((response) => {
        if (!isOk(response) || !response.data || !Array.isArray(response.data.requests)) return {ok: false};
        return {ok: true, requests: response.data.requests};
    });
}

/**
 * Pide el link para una tienda (`shop`, el dominio `myshopify`). `agencyClientId` es opcional: `null` desde
 * Conexiones, el cliente cuando se pide desde su ficha. Devuelve `{ok: true, request, outcome, notifyEmail}`
 * o `{ok: false, errorKey}`. Si salió bien, avisa que la lista cambió.
 */
export function requestShopifyLink({shop, agencyClientId = null}) {
    const body = {shop, agency_client_id: clientIdOf(agencyClientId)};
    return new APIClient(REQUESTS_URL).call('POST', body).then((response) => {
        if (!isOk(response) || !response.data || !response.data.request) {
            return {ok: false, errorKey: errorKeyOf(response)};
        }
        notifyShopifyLinkRequestsChanged();
        return {
            ok: true,
            request: response.data.request,
            outcome: response.data.outcome,
            notifyEmail: response.data.notify_email || null,
        };
    });
}

// --- «La lista de pedidos cambió» ------------------------------------------------------------------------
// El pedido se hace en un modal y el aviso de Conexiones vive en la pantalla de atrás: con este evento el
// aviso vuelve a pedir la lista sin que el modal sepa quién lo escucha (patrón de `utils/onboardingProgress.js`).

const CHANGED_EVENT = 'dashbo:shopify-link-requests-changed';

export function notifyShopifyLinkRequestsChanged() {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHANGED_EVENT));
}

/** Engancharse al aviso. Devuelve la función que lo desengancha. */
export function onShopifyLinkRequestsChanged(handler) {
    window.addEventListener(CHANGED_EVENT, handler);
    return () => window.removeEventListener(CHANGED_EVENT, handler);
}
